// Happily Village bar chart design tokens.
// Four theme colors map to the four pillars of Happily Village:
//   - university (purple, brand primary)
//   - townhall (cyan, brand secondary)
//   - runningtrack (orange, distance/effort)
//   - battle (red, competition)

export const HV_CHART_THEMES = {
  university: { accent: '#6C30CC', fill: 'rgba(108, 48, 204, 0.12)' },
  townhall: { accent: '#32C2D5', fill: 'rgba(50, 194, 213, 0.15)' },
  runningtrack: { accent: '#EF8011', fill: 'rgba(239, 128, 17, 0.15)' },
  battle: { accent: '#CC3030', fill: 'rgba(204, 48, 48, 0.12)' },
}

export const HV_CHART_NEUTRALS = {
  axisTick: '#9ca3af',
  gridLine: '#f0f1f5',
  dataLabel: '#2f2f2f',
}

// Returns a ready-to-spread Chart.js 3.x options object for a Happily Village
// bar chart. Pass the theme key and optional { datalabelFormatter } to
// override how numbers render above bars.
export const buildHVBarOptions = (themeKey, { datalabelFormatter } = {}) => {
  const theme = HV_CHART_THEMES[themeKey] || HV_CHART_THEMES.university
  return {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 16 } },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
      datalabels: {
        display: true,
        anchor: 'end',
        align: 'end',
        offset: 2,
        color: theme.accent,
        // The value labels are now the only place the number appears (the
        // y-axis is hidden), so give them a touch more weight.
        font: { size: 11, weight: 700 },
        ...(datalabelFormatter ? { formatter: datalabelFormatter } : {}),
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: {
          color: HV_CHART_NEUTRALS.axisTick,
          font: { size: 11, weight: 600 },
          // Keep labels horizontal — rotated labels eat vertical space and
          // read as cluttered. Autoskip thins them out when space is tight.
          maxRotation: 0,
          minRotation: 0,
          autoSkipPadding: 12,
        },
      },
      // Y axis hidden: every bar already carries its exact value as a label
      // above it, so the axis ticks + gridlines were redundant ink. Dropping
      // them gives a cleaner, more compact chart and frees horizontal room.
      // `grace` reserves a little headroom so the tallest bar's label never
      // clips the top.
      y: {
        display: false,
        beginAtZero: true,
        grace: '8%',
      },
    },
  }
}

// Returns a dataset stub for a bar chart in the given theme.
// Merge with label + data at the call site.
export const buildHVBarDataset = (themeKey) => {
  const theme = HV_CHART_THEMES[themeKey] || HV_CHART_THEMES.university
  return {
    backgroundColor: theme.fill,
    borderColor: theme.accent,
    borderWidth: 1.5,
    borderRadius: { topLeft: 6, topRight: 6, bottomLeft: 0, bottomRight: 0 },
    borderSkipped: false,
    maxBarThickness: 48,
    categoryPercentage: 0.6,
    barPercentage: 0.85,
  }
}
